import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { motion } from 'framer-motion';
import { Clock, ChevronLeft, ArrowRight, MessageSquare, MapPin, Phone } from 'lucide-react';
import { blogPosts } from "@/data/blog-posts";
import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { trackWhatsAppClick } from "@/lib/gtm";

export const Route = createFileRoute("/blog/$postId")({
  component: ArticlePage,
});

function ArticlePage() {
  const { postId } = useParams({ from: "/blog/$postId" });
  const post = blogPosts.find(p => p.id === postId);

  useEffect(() => {
    if (post) {
      // Schema JSON-LD
      const schemaData = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Article",
            "@id": `https://stopsomesquadrias.com.br/blog/${post.id}#article`,
            "headline": post.title,
            "description": post.metaDescription || post.excerpt,
            "image": `https://stopsomesquadrias.com.br${post.image}`,
            "author": {
              "@type": "Organization",
              "name": "STOP SOM Janelas Acústicas"
            },
            "publisher": {
              "@type": "Organization",
              "name": "STOP SOM Janelas Acústicas",
              "logo": {
                "@type": "ImageObject",
                "url": "https://stopsomesquadrias.com.br/logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://stopsomesquadrias.com.br/blog/${post.id}`
            }
          },
          {
            "@type": "LocalBusiness",
            "name": "STOP SOM Janelas Acústicas",
            "image": "https://stopsomesquadrias.com.br/logo.png",
            "@id": "https://stopsomesquadrias.com.br#localbusiness",
            "url": "https://stopsomesquadrias.com.br",
            "telephone": "+55 21 99269-8354",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Rua Itatinga, 511",
              "addressLocality": "Duque de Caxias",
              "addressRegion": "RJ",
              "postalCode": "25000-000",
              "addressCountry": "BR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": -22.7844,
              "longitude": -43.3117
            },
            "areaServed": {
              "@type": "State",
              "name": "Rio de Janeiro"
            },
            "description": "Empresa especializada em janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro, com fábrica em Duque de Caxias - RJ.",
            "keywords": "janelas acústicas no Rio de Janeiro, esquadrias acústicas no Rio de Janeiro, esquadrias antirruído, janelas antirruído, esquadrias sob medida, fábrica em Duque de Caxias"
          }
        ]
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schemaData);
      script.id = 'json-ld-schema';
      document.head.appendChild(script);

      return () => {
        const existingScript = document.getElementById('json-ld-schema');
        if (existingScript) existingScript.remove();
      };
    }
  }, [post]);

  if (!post) {
    return (
      <div className="pt-40 pb-20 text-center">
        <h1 className="text-2xl font-bold mb-4">Artigo não encontrado</h1>
        <Link to="/blog" className="text-primary hover:underline">Voltar ao Blog</Link>
      </div>
    );
  }

  const otherPosts = blogPosts.filter(p => p.id !== postId).slice(0, 3);

  const getCustomCTA = () => {
    switch (postId) {
      case 'como-reduzir-barulho-rua-apartamento':
        return {
          title: "Quer reduzir o barulho da rua no seu apartamento?",
          text: "Fale com a STOP SOM Janelas Acústicas e conheça soluções sob medida em janelas e esquadrias acústicas para ambientes no Rio de Janeiro."
        };
      case 'janela-acustica-funciona-mesmo':
        return {
          title: "Precisa de janelas ou esquadrias acústicas para reduzir ruídos externos?",
          text: "Solicite atendimento com a STOP SOM Janelas Acústicas no Rio de Janeiro."
        };
      case 'melhor-janela-acustica-para-quartos':
        return {
          title: "Quer mais silêncio e conforto no quarto?",
          text: "Fale com a STOP SOM Janelas Acústicas e conheça opções de janelas e esquadrias acústicas sob medida para ambientes no Rio de Janeiro."
        };
      default:
        return {
          title: "Gostou deste conteúdo?",
          text: "A STOP SOM é especialista em janelas acústicas e esquadrias acústicas. Transformamos seu ambiente em um refúgio de paz e silêncio em todo o Rio de Janeiro, com fábrica em Duque de Caxias."
        };
    }
  };

  const customCTA = getCustomCTA();

  return (
    <>
      <SEO 
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        ogImage={`https://stopsomesquadrias.com.br${post.image}`}
        ogUrl={`https://stopsomesquadrias.com.br/blog/${post.id}`}
      />
      <div className="bg-white">
      {/* Article Header */}
      <section className="pt-32 md:pt-40 pb-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-secondary font-semibold mb-8 transition-colors">
            <ChevronLeft size={20} /> Voltar ao Blog
          </Link>
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
              <span className="bg-accent text-primary px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">{post.category}</span>
              <span className="flex items-center gap-1.5"><Clock size={16} /> {post.readingTime}</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="text-xl text-gray-600 mb-8 leading-relaxed italic">
                {post.subtitle}
              </p>
            )}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold">SS</div>
              <div>
                <p className="font-bold text-primary text-lg">Equipe STOP SOM</p>
                <p className="text-gray-500">Especialistas em Acústica</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 md:py-20 px-6">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row gap-16">
            <div className="lg:w-2/3">
              <div className="rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-2xl mb-12 aspect-video">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  width="1200"
                  height="675"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="prose prose-lg max-w-none prose-headings:text-primary prose-p:text-gray-600 prose-p:leading-relaxed prose-strong:text-primary mb-16">
                {post.content.map((item, index) => {
                  if (item.type === 'h2') {
                    return <h2 key={index} className="text-2xl md:text-3xl font-bold mt-12 mb-6">{item.text}</h2>;
                  }
                  return <p key={index} className="mb-6">{item.text}</p>;
                })}

                <div className="mt-12 p-8 bg-gray-50 rounded-3xl border border-gray-100 shadow-sm">
                  <h3 className="text-2xl font-bold text-primary mb-4">{customCTA.title}</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    {customCTA.text}
                  </p>
                  <a 
                    href={`https://wa.me/5521992698354?text=Olá, tudo bem? Estava no site lendo o artigo "${post.title}" e preciso de um orçamento.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick("Falar com especialista no WhatsApp", `https://wa.me/5521992698354?text=Olá, tudo bem? Estava no site lendo o artigo "${post.title}" e preciso de um orçamento.`, "blog")}
                    data-gtm-event="whatsapp_click"
                    data-conversion="whatsapp"
                    data-button-location="blog"
                    className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-xl font-bold hover:bg-primary transition-all shadow-lg hover:shadow-xl w-full sm:w-auto justify-center"
                  >
                    Falar com especialista no WhatsApp <MessageSquare size={20} />
                  </a>
                </div>
              </div>

              {/* Bloco NAP/Local */}
              <div className="bg-primary/5 rounded-[2.5rem] p-8 md:p-12 border border-primary/10">
                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-6">Atendimento em janelas e esquadrias acústicas no Rio de Janeiro</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  A STOP SOM Janelas Acústicas atende clientes em todo o Rio de Janeiro com soluções sob medida em janelas e esquadrias acústicas para reduzir ruídos externos e melhorar o conforto de residências, apartamentos, escritórios e ambientes comerciais. Nossa fábrica está localizada em Duque de Caxias - RJ.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-8 mb-10">
                  <div className="space-y-4">
                    <p className="font-bold text-primary flex items-center gap-2 uppercase tracking-wide text-sm">
                      <MapPin size={18} className="text-secondary" /> Localização
                    </p>
                    <div>
                      <p className="text-gray-900 font-semibold">STOP SOM Janelas Acústicas</p>
                      <p className="text-gray-600">Fábrica: Rua Itatinga, 511 — Duque de Caxias - RJ</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <p className="font-bold text-primary flex items-center gap-2 uppercase tracking-wide text-sm">
                      <Phone size={18} className="text-secondary" /> Contato
                    </p>
                    <div>
                      <p className="text-gray-900 font-semibold">WhatsApp e Telefone</p>
                      <p className="text-gray-600">(21) 99269-8354</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-primary/10 pt-10">
                  <p className="text-xl font-bold text-primary mb-6">Precisa de janelas ou esquadrias acústicas no Rio de Janeiro?</p>
                  <p className="text-gray-600 mb-8">Fale com a STOP SOM Janelas Acústicas pelo WhatsApp e solicite atendimento especializado para o seu projeto em qualquer região do RJ.</p>
                  <a 
                    href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento."
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick("Falar no WhatsApp", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "blog")}
                    data-gtm-event="whatsapp_click"
                    data-conversion="whatsapp"
                    data-button-location="blog"
                    className="inline-flex items-center gap-2 bg-accent text-primary px-10 py-5 rounded-2xl font-bold hover:bg-white transition-all shadow-md hover:shadow-lg w-full sm:w-auto justify-center"
                  >
                    Falar no WhatsApp <MessageSquare size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-1/3">
              <div className="sticky top-32 space-y-8">
                <div className="bg-primary text-white p-10 rounded-[2.5rem] md:rounded-[3rem] shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-accent/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <h3 className="text-2xl font-bold mb-6 relative z-10">Precisa de silêncio no seu ambiente?</h3>
                  <p className="opacity-80 mb-8 relative z-10">Solicite um orçamento agora mesmo e descubra como podemos transformar seu ambiente com esquadrias acústicas sob medida.</p>
                  <Link 
                    to="/orcamento" 
                    onClick={() => trackWhatsAppClick("Solicitar Orçamento", "/orcamento", "blog-sidebar")}
                    data-gtm-event="whatsapp_click"
                    data-conversion="whatsapp"
                    data-button-location="blog-sidebar"
                    className="w-full bg-accent text-primary py-4 rounded-xl font-bold text-center inline-block hover:bg-white transition-all relative z-10"
                  >
                    Solicitar Orçamento
                  </Link>
                </div>

                <div className="bg-gray-50 p-10 rounded-[2.5rem] md:rounded-[3rem] border border-gray-100">
                  <h3 className="text-xl font-bold text-primary mb-6">Outros Artigos</h3>
                  <div className="space-y-6">
                    {otherPosts.map((otherPost) => (
                      <Link key={otherPost.id} to={`/blog/${otherPost.id}`} className="group block">
                        <h4 className="font-bold text-gray-800 group-hover:text-secondary transition-colors mb-2">{otherPost.title}</h4>
                        <span className="text-sm text-gray-500 flex items-center gap-1">Ler mais <ArrowRight size={14} /></span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}